import React, { useState, useEffect } from "react";
import {
  Drawer,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Flex,
  Checkbox,
  Divider,
  Tag,
  Empty,
  Spin,
  message,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllModulesAction,
  saveEntityModulesAction,
  getEntityModulesAction,
} from "../../../../../../../store/action/admin/moduleActions";
import { getUserRoleAction } from "../../../../../../../store/action/admin/userRoleActions";
import { css } from "@emotion/css";
import BrokenBuildingIcon from "../../../../../../../Theme/CustomIcons/Icons/Building/BrokenBuildingIcon";
import CalendarIcon from "../../../../../../../Theme/CustomIcons/Icons/Time/CalendarIcon";
import CustomGridIcon from "../../../../../../../Theme/CustomIcons/Icons/CustomIcons/CustomGridIcon";
import ProfileIcon from "../../../../../../../Theme/CustomIcons/Icons/Users/ProfileIcon";
import EmptyWalletIcon from "../../../../../../../Theme/CustomIcons/Icons/Money/EmptyWalletIcon";

const { Title, Text } = Typography;

const getModuleIcon = (name) => {
  switch (name.toLowerCase()) {
    case "payroll":
      return <EmptyWalletIcon size="large" />;
    case "ontrack":
      return <CalendarIcon size="large" />;
    case "auth":
      return <CustomGridIcon size="large" />;
    case "loan":
      return <BrokenBuildingIcon size="large" />;
    case "hr":
      return <ProfileIcon size="large" />;
    default:
      return <CustomGridIcon size="large" />;
  }
};

const ModulesDrawer = ({ open, onClose, entityId }) => {
  const dispatch = useDispatch();
  const themeDefault = useSelector((state) => state.themeDefault);
  const colorPrimary = themeDefault?.info?.colorPrimary || "#1890ff";

  const getUserRoleList = useSelector((state) => state.getUserRoleList);
  const { getUserRoleInfo, getUserRoleLoading } = getUserRoleList;

  const getAllModulesList = useSelector((state) => state.getAllModulesList);
  const { loading: modulesLoading, modules } = getAllModulesList;

  const getEntityModulesList = useSelector((state) => state.getEntityModulesList);
  const { loading: getLoading, selectedModuleRoles: initialRoles } = getEntityModulesList;

  const saveEntityModulesList = useSelector((state) => state.saveEntityModulesList);
  const { loading: saveLoading, success: saveSuccess, error: saveError } = saveEntityModulesList;

  const [selectedModuleRoles, setSelectedModuleRoles] = useState({});

  useEffect(() => {
    if (open) {
      dispatch(getUserRoleAction());
      dispatch(getAllModulesAction());
      if (entityId) {
        dispatch(getEntityModulesAction(entityId));
      }
    }
  }, [open, dispatch, entityId]);

  useEffect(() => {
    if (initialRoles) {
      setSelectedModuleRoles(initialRoles);
    }
  }, [initialRoles]);

  useEffect(() => {
    if (saveSuccess) {
      message.success("Module roles saved successfully!");
      onClose();
    }
    if (saveError) {
      message.error(saveError);
    }
  }, [saveSuccess, saveError, onClose]);

  const handleSave = () => {
    dispatch(saveEntityModulesAction(entityId, selectedModuleRoles));
  };

  const toggleModule = (modId) => {
    setSelectedModuleRoles((prev) => {
      const next = { ...prev };
      if (next[modId]) {
        delete next[modId];
      } else {
        next[modId] = [];
      }
      return next;
    });
  };

  const toggleRole = (modId, roleId) => {
    setSelectedModuleRoles((prev) => {
      const next = { ...prev };
      const roles = next[modId] || [];
      if (roles.includes(roleId)) {
        next[modId] = roles.filter((id) => id !== roleId);
      } else {
        next[modId] = [...roles, roleId];
      }
      return next;
    });
  };

  const isModuleSelected = (modId) => !!selectedModuleRoles[modId];

  return (
    <Drawer
      title={
        <Title level={4} style={{ margin: 0 }}>
          Entity Module Access & Roles
        </Title>
      }
      placement="right"
      open={open}
      onClose={onClose}
      width="100%"
      destroyOnClose
      footer={
        <Space
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saveLoading}>
            Save Module Roles
          </Button>
        </Space>
      }
    >
      <Row justify="center">
        <Col span={18}>
          <Card
            title={
              <Flex justify="space-between" align="center">
                <span>Configure Modules and Roles</span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Enable modules and assign specific roles for this entity
                </Text>
              </Flex>
            }
            bordered
          >
            {modulesLoading || getLoading ? (
              <Flex justify="center" align="center" style={{ padding: "50px" }}>
                <Spin size="large" />
              </Flex>
            ) : (
              <Row gutter={[24, 24]}>
                {modules?.map((mod) => (
                  <Col span={24} key={mod._id}>
                    <Card
                      className={css`
                        border: 1px solid ${isModuleSelected(mod._id) ? colorPrimary : "#f0f0f0"};
                        transition: all 0.3s ease;
                        .ant-card-body {
                          padding: 16px;
                        }
                      `}
                    >
                      <Flex vertical gap={16}>
                        <Flex align="center" justify="space-between">
                          <Flex align="center" gap={12}>
                            <div
                              className={css`
                                background: ${isModuleSelected(mod._id) ? `${colorPrimary}15` : "#f5f5f5"};
                                padding: 8px;
                                border-radius: 8px;
                                color: ${isModuleSelected(mod._id) ? colorPrimary : "#bfbfbf"};
                              `}
                            >
                              {getModuleIcon(mod.module_name)}
                            </div>
                            <Flex vertical>
                              <Text style={{ fontWeight: 700, fontSize: 16 }}>
                                {mod.module_name}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {mod.description}
                              </Text>
                            </Flex>
                          </Flex>
                          <Checkbox
                            checked={isModuleSelected(mod._id)}
                            onChange={() => toggleModule(mod._id)}
                            style={{ transform: "scale(1.2)" }}
                          >
                            Enable
                          </Checkbox>
                        </Flex>

                        {isModuleSelected(mod._id) && (
                          <div
                            className={css`
                              background: #fafafa;
                              padding: 16px;
                              border-radius: 8px;
                              border-top: 1px solid #f0f0f0;
                            `}
                          >
                            <Title level={5} style={{ fontSize: 14, marginBottom: 12 }}>
                              Available Roles
                            </Title>
                            {getUserRoleLoading ? (
                              <Text>Loading roles...</Text>
                            ) : getUserRoleInfo?.length > 0 ? (
                              <Row gutter={[16, 12]}>
                                {getUserRoleInfo.map((role) => (
                                  <Col span={6} key={role._id}>
                                    <Checkbox
                                      checked={(selectedModuleRoles[mod._id] || []).includes(role._id)}
                                      onChange={() => toggleRole(mod._id, role._id)}
                                    >
                                      <Text style={{ fontSize: 13 }}>{role.title}</Text>
                                    </Checkbox>
                                  </Col>
                                ))}
                              </Row>
                            ) : (
                              <Flex vertical align="center" padding={16}>
                                <Text type="secondary">No roles found. Please create roles in User Settings first.</Text>
                                <Button type="link" size="small">Create Roles</Button>
                              </Flex>
                            )}
                          </div>
                        )}
                      </Flex>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Card>
        </Col>
      </Row>
    </Drawer>
  );
};

export default ModulesDrawer;
